import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { toast } from "sonner";
import { api, errMsg, fmtDate } from "@/lib/api";
import { useSeo, PRIVATE_SEO } from "@/lib/seo";

export default function AdminAppealsPage() {
  useSeo({ title: "Appeals | LUX", ...PRIVATE_SEO });
  const [status, setStatus] = useState("open");
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["admin", "appeals", status],
    queryFn: async () => (await api.get(`/admin/appeals?status=${status}`)).data,
  });

  const decide = async (id, decision) => {
    const note = window.prompt(`Explain your decision to ${decision} the appeal (shown to the advertiser):`);
    if (!note || note.length < 5) return;
    try {
      await api.post(`/admin/appeals/${id}/decide`, { decision, note });
      toast.success(`Appeal ${decision === "overturn" ? "overturned" : "upheld"}`);
      await refetch();
    } catch (err) {
      toast.error(errMsg(err));
    }
  };

  return (
    <div className="space-y-5" data-testid="admin-appeals-page">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="adm-label">Appeals</p>
          <h1 className="mt-2 text-3xl font-semibold text-admin-text">Suspension appeals</h1>
          <p className="mt-1 text-sm text-admin-muted">Reviewed by a different staff member than the one who suspended.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-admin-border bg-admin-surface p-1">
          {["open", "upheld", "overturned", "all"].map((s) => (
            <button key={s} onClick={() => setStatus(s)}
              className={`rounded-full px-3 py-1.5 text-sm transition-colors ${status === s ? "bg-[#111827] text-white" : "text-admin-text hover:bg-admin-surface2"}`}
              data-testid={`admin-appeals-status-${s}`}>
              {s.charAt(0).toUpperCase() + s.slice(1)}
            </button>
          ))}
        </div>
      </header>
      {isLoading ? <p className="text-sm text-admin-muted">Loading...</p> : !data?.appeals?.length ? (
        <section className="adm-card p-10 text-center text-admin-muted" data-testid="admin-appeals-empty">
          No appeals in this view.
        </section>
      ) : (
        <ul className="space-y-3">
          {data.appeals.map((a) => (
            <li key={a.id} className="adm-card p-5" data-testid="admin-appeals-item">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <p className="text-sm font-semibold text-admin-text">{a.profile_slug || "Account"}</p>
                  <p className="mt-1 text-xs text-admin-muted">Suspended by {a.suspended_by_name} · Reason: {a.suspension_reason || "—"}</p>
                  <p className="mt-3 text-sm text-admin-text">{a.message}</p>
                  <p className="mt-2 text-xs text-admin-muted">Submitted {fmtDate(a.created_at, true)} · Status: {a.status}</p>
                  {a.decision_note && <p className="mt-2 text-xs text-admin-text">Decision: {a.decision_note}</p>}
                </div>
                {a.status === "open" && a.can_review && (
                  <div className="flex gap-2">
                    <button onClick={() => decide(a.id, "overturn")} className="adm-btn adm-btn-primary" data-testid="admin-appeals-overturn">Overturn</button>
                    <button onClick={() => decide(a.id, "uphold")} className="adm-btn adm-btn-outline" data-testid="admin-appeals-uphold">Uphold</button>
                  </div>
                )}
                {a.status === "open" && !a.can_review && (
                  <p className="text-xs text-admin-muted">You can't review (you suspended this account).</p>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
